/**
 * CellarScience dry yeast — MoreFlavor/MoreBeer's house dry-yeast line.
 *
 * Specs are taken from the published strain sheets (temperature in °F
 * converted to °C and rounded). Alcohol tolerance printed as a band keeps
 * the upper figure. STA1 follows the lab's own STA-1 flag.
 */

import type { Yeast } from '../../schema/yeast';

/** CellarScience dry cultures. @see Yeast */
export const cellarScienceYeasts: readonly Yeast[] = [
  {
    id: 'cellarscience-cali',
    lab: 'CellarScience',
    productCode: 'CALI',
    name: 'CALI',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 84 },
    temperatureC: { min: 15, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      'A Chico-type American ale: clean, hop-friendly and happy in anything from pale ale to stout. Direct-pitchable, which is the point of the line — sprinkle it on and leave it alone.',
    tags: ['clean', 'american', 'chico', 'ipa', 'pale-ale', 'west-coast'],
    url: 'https://cellarscience.com/products/cali-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-english',
    lab: 'CellarScience',
    productCode: 'ENGLISH',
    name: 'ENGLISH',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 83 },
    temperatureC: { min: 16, max: 21 },
    flocculation: 'high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A fast English ale that drops a compact cake and finishes drier than a classic ESB strain. Fruity enough for a bitter, clean enough for an American pale if you hold it cool.',
    tags: ['english', 'bitter', 'fast', 'clearing', 'pale-ale'],
    url: 'https://cellarscience.com/products/english-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-hazy',
    lab: 'CellarScience',
    productCode: 'HAZY',
    name: 'HAZY',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 17, max: 24 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A London Fog-type hazy IPA strain: peach, citrus, mango and passionfruit esters that get louder as you warm it, with a haze that survives the crash. The dry-yeast answer when you cannot get Verdant or WLP066 on the shelf.',
    tags: ['hazy', 'ipa', 'stone-fruit', 'tropical', 'new-england', 'esters'],
    url: 'https://cellarscience.com/products/hazy-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-nectar',
    lab: 'CellarScience',
    productCode: 'NECTAR',
    name: 'NECTAR',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A UK-pedigree hop yeast that is not a copy of a famous parent: fresh malt, citrus and floral esters, and enough biotransformation to suit pale ales and IPAs. Quieter than HAZY, fruitier than CALI.',
    tags: ['ipa', 'pale-ale', 'english', 'citrus', 'floral', 'hop-forward'],
    url: 'https://cellarscience.com/products/nectar-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-voss',
    lab: 'CellarScience',
    productCode: 'VOSS',
    name: 'VOSS',
    form: 'dry',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 20, max: 40 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      'Dried Voss kveik: orange-peel citrus, a hard floc and a two-or-three-day finish at the hot end of the range. Alcohol tolerance is unusually high even for kveik, so it will take a strong ale as well as a pale.',
    tags: ['kveik', 'voss', 'orange', 'norwegian', 'fast', 'high-gravity'],
    url: 'https://cellarscience.com/products/voss-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-hornindal',
    lab: 'CellarScience',
    productCode: 'HORNINDAL',
    name: 'HORNINDAL',
    form: 'dry',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 23, max: 37 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Dried Hornindal kveik: stone fruit and tropical notes rather than Voss orange, equally fast when held hot. The obvious CellarScience pick for a hazy IPA without temperature control.',
    tags: ['kveik', 'hornindal', 'tropical', 'stone-fruit', 'norwegian', 'fast', 'ipa'],
    url: 'https://cellarscience.com/products/hornindal-dry-beer-yeast',
    provenance: 'representative',
  },
  {
    id: 'cellarscience-kolsch',
    lab: 'CellarScience',
    productCode: 'KOLSCH',
    name: 'KÖLSCH',
    form: 'dry',
    type: 'hybrid',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 16, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      'A kölsch hybrid: clean, dry and faintly pear-citrus, with noble hops and pilsner malt left room to show. Cold condition it after primary or it will not taste finished.',
    tags: ['kolsch', 'hybrid', 'clean', 'german', 'crisp'],
    url: 'https://cellarscience.com/products/kolsch-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-monk',
    lab: 'CellarScience',
    productCode: 'MONK',
    name: 'MONK',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 16, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A Belgian abbey strain for blonde through quadrupel: dried fruit, restrained phenols and enough gravity tolerance to finish a strong ale. Cleaner than T-58, fruitier than a neutral American yeast.',
    tags: ['belgian', 'abbey', 'dubbel', 'tripel', 'esters', 'high-gravity'],
    url: 'https://cellarscience.com/products/monk-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-saison',
    lab: 'CellarScience',
    productCode: 'SAISON',
    name: 'SAISON',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 85, max: 95 },
    temperatureC: { min: 17, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      'A diastatic saison: pepper, citrus and a finish that will keep eating dextrins until there is almost nothing left. Genuinely STA1 positive — never assume a bottled batch has stopped, and treat shared plastic as contaminated.',
    tags: ['saison', 'diastatic', 'farmhouse', 'phenolic', 'dry-finish'],
    url: 'https://cellarscience.com/products/saison-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-german',
    lab: 'CellarScience',
    productCode: 'GERMAN',
    name: 'GERMAN',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 78, max: 85 },
    temperatureC: { min: 10, max: 15 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'Weihenstephan 34/70 in a CellarScience sachet: clean, balanced and usable from pilsner to doppelbock. The cheap dry-lager default when you would otherwise reach for W-34/70.',
    tags: ['lager', 'german', 'pilsner', 'helles', 'clean', 'w34-70'],
    url: 'https://cellarscience.com/products/german-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-berlin',
    lab: 'CellarScience',
    productCode: 'BERLIN',
    name: 'BERLIN',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 10, max: 15 },
    flocculation: 'high',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      'A fruitier European lager strain in the S-23 family: pear and berry esters over soft malt, rather than a perfectly neutral pilsner profile. Ferment at the bottom of the range and lager it properly or the fruit stays in the glass.',
    tags: ['lager', 'fruity', 'european-lager', 'marzen', 'esters'],
    url: 'https://cellarscience.com/products/berlin-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-baja',
    lab: 'CellarScience',
    productCode: 'BAJA',
    name: 'BAJA',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      'A Mexican-lager strain in the Modelo family: clean, dry and crushable, with malt and hops in balance rather than a German sulfur note. The dry-yeast pick for a light lager, dark lager or cold IPA.',
    tags: ['lager', 'mexican', 'clean', 'dry', 'cold-ipa', 'light-lager'],
    url: 'https://cellarscience.com/products/baja-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-acid',
    lab: 'CellarScience',
    productCode: 'ACID',
    name: 'ACID',
    form: 'dry',
    type: 'wild',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 19, max: 25 },
    flocculation: 'high',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      'A Lachancea yeast that produces lactic acid and alcohol in the same vessel, so a sour beer needs no kettle-souring step and no bacteria. Acidity is pitch-rate dependent — underpitch if you want it tart. Same idea as Philly Sour, in a cheaper sachet.',
    tags: ['sour', 'lactic', 'wild', 'non-saccharomyces', 'gose', 'berliner-weisse'],
    url: 'https://cellarscience.com/products/acid-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-big-ben',
    lab: 'CellarScience',
    productCode: 'BIG BEN',
    name: 'BIG BEN',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 13, max: 26 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A neutral English ale strain that gets out of the way: pale ales, ESBs, porters and stouts, fermenting anywhere from 13 to 26 °C. CellarScience publish attenuation, temperature and alcohol tolerance but no flocculation figure, so that one is typical for the class.',
    tags: ['english', 'neutral', 'pale-ale', 'esb', 'porter', 'workhorse'],
    url: 'https://cellarscience.com/products/big-ben-dry-beer-yeast',
    provenance: 'representative',
  },
  {
    id: 'cellarscience-hefe',
    lab: 'CellarScience',
    productCode: 'HEFE',
    name: 'HEFE',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 80, max: 91 },
    temperatureC: { min: 15, max: 25 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'The weissbier strain in CellarScience’s dry range: banana and clove, attenuating as far as 91 percent, and flocculating low so the beer stays cloudy the way the style wants.',
    tags: ['weissbier', 'hefeweizen', 'banana', 'clove', 'german', 'low-flocculation'],
    url: 'https://cellarscience.com/products/hefe-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-jungle',
    lab: 'CellarScience',
    productCode: 'JUNGLE',
    name: 'JUNGLE',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 18, max: 32 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'Selected for hop biotransformation: guava, passion fruit and mango on top of whatever the hops already bring. It takes 15 percent alcohol and 12 PSI of pressure, so it suits a hot, fast, hop-forward fermentation. Flocculation is not published.',
    tags: ['biotransformation', 'tropical', 'hazy', 'pressure-tolerant', 'guava', 'ipa'],
    url: 'https://cellarscience.com/products/jungle-dry-beer-yeast',
    provenance: 'representative',
  },
  {
    id: 'cellarscience-maison',
    lab: 'CellarScience',
    productCode: 'MAISON',
    name: 'MAISON',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A saison strain for the cooler end of the style: expressive at 24 °C without the sulfur or the stall a classic Belgian saison yeast can bring. Low to medium flocculation leaves it hazy and soft.',
    tags: ['saison', 'farmhouse', 'belgian', 'expressive', 'peppery'],
    url: 'https://cellarscience.com/products/maison-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-matterhorn',
    lab: 'CellarScience',
    productCode: 'MATTERHORN',
    name: 'MATTERHORN',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 79, max: 83 },
    temperatureC: { min: 9, max: 14 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A clean everyday lager strain: well attenuated at 79 to 83 percent, minimal fermentation signature, and a proper cold band at 9 to 14 °C. The one for a house helles or pilsner that should taste of malt and hops only.',
    tags: ['lager', 'clean', 'helles', 'pilsner', 'crisp', 'cold'],
    url: 'https://cellarscience.com/products/munich-dry-beer-yeast-copy',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-munich',
    lab: 'CellarScience',
    productCode: 'MUNICH',
    name: 'MUNICH',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 10, max: 18 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10.5,
    sta1: null,
    description:
      'A Munich lager strain with a wider band than most, workable from 10 to 18 °C, and attenuation held back to 75 to 80 percent so the malt stays in the glass. For dunkel, helles and märzen.',
    tags: ['lager', 'munich', 'malty', 'dunkel', 'marzen', 'german'],
    url: 'https://cellarscience.com/products/munich-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-ne-hazy',
    lab: 'CellarScience',
    productCode: 'NE HAZY',
    name: 'NE HAZY',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 17, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'The New England strain of the range, and the low flocculation is the product rather than a side effect: it holds haze, tolerates pressure, and finishes soft at 75 to 80 percent. Distinct from CellarScience HAZY, which is its own strain.',
    tags: ['neipa', 'hazy', 'haze-retention', 'juicy', 'pressure-tolerant', 'low-flocculation'],
    url: 'https://cellarscience.com/products/ne-hazy-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-prime',
    lab: 'CellarScience',
    productCode: 'PRIME',
    name: 'PRIME',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 12, max: 25 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A bottle-conditioning strain, not a primary one: neutral, fast, and flocculating hard enough to leave a compact sediment you can pour past. It attenuates 95 to 100 percent because it is working on priming sugar rather than wort, and it is gluten-free.',
    tags: ['bottle-conditioning', 'refermentation', 'neutral', 'gluten-free', 'cask', 'priming'],
    url: 'https://cellarscience.com/products/prime-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-reaper',
    lab: 'CellarScience',
    productCode: 'REAPER',
    name: 'REAPER',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 78, max: 93 },
    temperatureC: { min: 24, max: 29 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 20,
    sta1: null,
    description:
      'The high-gravity strain: rated to 20 percent ABV and happiest hot, from 24 to 29 °C. For barleywine, imperial stout and anything else that would stall an ordinary ale yeast halfway.',
    tags: ['high-gravity', 'barleywine', 'imperial-stout', 'strong-ale', 'hot-ferment'],
    url: 'https://cellarscience.com/products/reaper-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-rokkar',
    lab: 'CellarScience',
    productCode: 'RØKKAR',
    name: 'RØKKAR',
    form: 'dry',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 83 },
    temperatureC: { min: 18, max: 35 },
    flocculation: 'high',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A kveik selected for clean speed rather than for farmhouse funk: balanced fruit, fast maturation, and a dense cake that drops the beer bright. It runs from 18 all the way to 35 °C and takes 15 percent alcohol, which is the kveik bargain — ferment hot, package sooner.',
    tags: ['kveik', 'norwegian', 'fast', 'clean', 'high-temperature', 'ipa'],
    url: 'https://cellarscience.com/products/rokkar-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-velo',
    lab: 'CellarScience',
    productCode: 'VELO',
    name: 'VELO',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 83 },
    temperatureC: { min: 16, max: 34 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A neutral house strain that doubles as a seltzer yeast: very low fusel and sulfur, quick through simple sugars, and clean across a 16 to 34 °C band. Where CALI needs temperature control, this one does not.',
    tags: ['neutral', 'clean', 'seltzer', 'house-strain', 'fast', 'wide-range'],
    url: 'https://cellarscience.com/products/velo-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'cellarscience-west-coast',
    lab: 'CellarScience',
    productCode: 'WEST COAST',
    name: 'WEST COAST',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 84 },
    temperatureC: { min: 15, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'The dry West Coast ale strain: attenuates to 84 percent for a finish that reads dry rather than sweet, drops medium to high so the beer clears, and leaves the hops to do the talking.',
    tags: ['west-coast', 'ipa', 'clean', 'dry-finish', 'american', 'pale-ale'],
    url: 'https://cellarscience.com/products/west-coast-dry-beer-yeast',
    provenance: 'spec-sheet',
  },
];
